import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AdminPanelSettings as AdminIcon,
  AssignmentInd as AssignUserIcon,
  TableChart as SheetsIcon
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import GoogleSheetsIntegration from './GoogleSheetsIntegration';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function AdminPanel({ open, onClose }) {
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // User form state
  const [userDialog, setUserDialog] = useState(false);
  const [userForm, setUserForm] = useState({
    email: '',
    username: '',
    password: '',
    is_admin: false
  });
  
  // Project form state
  const [projectDialog, setProjectDialog] = useState(false);
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    tracking_logic: 'UKBZT',
    enable_google_sheets: false,
    tracking_credentials: {
      shipstation_api_key: '',
      delivery_api_key: '',
      delivery_api_token: '',
      fedex_client_id: '',
      fedex_client_secret: '',
      // Aramex credentials for UAET (pre-populated)
      aramex_username: 'harshsinghmaurya@trishoolinhouse.com',
      aramex_password: 'Jpharen@33',
      aramex_account_number: '72012891',
      aramex_account_pin: '285290',
      aramex_account_entity: 'DXB',
      aramex_account_country: 'AE',
      // EasyPost credentials for EZPOST (pre-populated)
      easypost_api_key: 'EZAKe88443ddebb340edaed96e3c4a4175bd7Mf7ldqCCkPoVhSyEa3W7Q'
    }
  });
  
  // Edit project state
  const [editProjectDialog, setEditProjectDialog] = useState(false);
  const [editProjectForm, setEditProjectForm] = useState({
    id: null,
    name: '',
    description: ''
  });
  
  // Handler for tracking logic changes
  const handleTrackingLogicChange = (newLogic) => {
    let updatedCredentials = { ...projectForm.tracking_credentials };
    
    // Auto-populate Aramex credentials when UAET is selected
    if (newLogic === 'UAET') {
      updatedCredentials = {
        ...updatedCredentials,
        aramex_username: 'harshsinghmaurya@trishoolinhouse.com',
        aramex_password: 'Jpharen@33',
        aramex_account_number: '72012891',
        aramex_account_pin: '285290',
        aramex_account_entity: 'DXB',
        aramex_account_country: 'AE'
      };
    }
    
    // Auto-populate EasyPost credentials when EZPOST is selected
    if (newLogic === 'EZPOST') {
      updatedCredentials = {
        ...updatedCredentials,
        easypost_api_key: 'EZAKe88443ddebb340edaed96e3c4a4175bd7Mf7ldqCCkPoVhSyEa3W7Q'
      };
    }
    
    // Auto-populate both FedEx and Aramex credentials when fedUAE is selected
    if (newLogic === 'fedUAE') {
      updatedCredentials = {
        ...updatedCredentials,
        // FedEx credentials for AWB tracking
        fedex_client_id: '',
        fedex_client_secret: '',
        // Aramex credentials for shipment tracking
        aramex_username: 'harshsinghmaurya@trishoolinhouse.com',
        aramex_password: 'Jpharen@33',
        aramex_account_number: '72012891',
        aramex_account_pin: '285290',
        aramex_account_entity: 'DXB',
        aramex_account_country: 'AE'
      };
    }
    
    setProjectForm({ 
      ...projectForm, 
      tracking_logic: newLogic,
      tracking_credentials: updatedCredentials
    });
  };
  
  // User assignment state
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignForm, setAssignForm] = useState({
    user_id: '',
    project_id: '',
    role: 'user'
  });
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    if (open) {
      loadUsers();
      loadProjects();
      loadAssignments();
    }
  }, [open]);

  const loadUsers = async () => {
    try {
      const data = await apiService.getUsers();
      setUsers(data);
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to load users: ${error.message}` });
    }
  };

  const loadProjects = async () => {
    try {
      const data = await apiService.getAdminProjects();
      setProjects(data);
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to load projects: ${error.message}` });
    }
  };

  const loadAssignments = async () => {
    try {
      const data = await apiService.getAssignments();
      setAssignments(data);
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to load assignments: ${error.message}` });
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.deleteProject(projectId);
      setMessage({ type: 'success', text: 'Project deleted successfully!' });
      loadProjects();
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to delete project: ${error.message}` });
    }
  };

  const handleRemoveAssignment = async (assignment) => {
    if (!window.confirm(`Remove ${assignment.user_name} from ${assignment.project_name}?`)) {
      return;
    }

    try {
      await apiService.removeUserFromProject({
        user_id: assignment.user_id,
        project_id: assignment.project_id
      });
      setMessage({ type: 'success', text: 'User removed from project successfully!' });
      loadAssignments();
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to remove assignment: ${error.message}` });
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user "${userEmail}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiService.deleteUser(userId);
      setMessage({ type: 'success', text: 'User deleted successfully!' });
      loadUsers();
      loadAssignments(); // Refresh assignments as well since user assignments will be removed
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to delete user: ${error.message}` });
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.email || !userForm.username || !userForm.password) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    try {
      await apiService.createUser(userForm);
      setMessage({ type: 'success', text: 'User created successfully!' });
      setUserDialog(false);
      setUserForm({ email: '', username: '', password: '', is_admin: false });
      loadUsers();
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to create user: ${error.message}` });
    }
  };

  const handleCreateProject = async () => {
    if (!projectForm.name) {
      setMessage({ type: 'error', text: 'Please enter a project name' });
      return;
    }

    try {
      await apiService.createProject(projectForm);
      setMessage({ type: 'success', text: 'Project created successfully!' });
      setProjectDialog(false);
      setProjectForm({ 
        name: '', 
        description: '',
        tracking_logic: 'UKBZT',
        enable_google_sheets: false,
        tracking_credentials: {
          shipstation_api_key: '',
          delivery_api_key: '',
          delivery_api_token: '',
          fedex_client_id: '',
          fedex_client_secret: '',
          // Aramex credentials for UAET (pre-populated)
          aramex_username: 'harshsinghmaurya@trishoolinhouse.com',
          aramex_password: 'Jpharen@33',
          aramex_account_number: '72012891',
          aramex_account_pin: '285290',
          aramex_account_entity: 'DXB',
          aramex_account_country: 'AE',
          // EasyPost credentials for EZPOST (pre-populated)
          easypost_api_key: 'EZAKe88443ddebb340edaed96e3c4a4175bd7Mf7ldqCCkPoVhSyEa3W7Q'
        }
      });
      loadProjects();
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to create project: ${error.message}` });
    }
  };

  const handleEditProject = (project) => {
    setEditProjectForm({
      id: project.id,
      name: project.name,
      description: project.description || ''
    });
    setEditProjectDialog(true);
  };

  const handleUpdateProject = async () => {
    if (!editProjectForm.name) {
      setMessage({ type: 'error', text: 'Please enter a project name' });
      return;
    }

    try {
      await apiService.updateProject(editProjectForm.id, {
        name: editProjectForm.name,
        description: editProjectForm.description
      });
      setMessage({ type: 'success', text: 'Project updated successfully!' });
      setEditProjectDialog(false);
      setEditProjectForm({ id: null, name: '', description: '' });
      loadProjects();
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to update project: ${error.message}` });
    }
  };

  const handleAssignUser = async () => {
    if (!assignForm.user_id || !assignForm.project_id) {
      setMessage({ type: 'error', text: 'Please select both user and project' });
      return;
    }

    try {
      await apiService.assignUserToProject(assignForm);
      setMessage({ type: 'success', text: 'User assigned to project successfully!' });
      setAssignDialog(false);
      setAssignForm({ user_id: '', project_id: '', role: 'user' });
      loadAssignments(); // Reload assignments after creating new one
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to assign user: ${error.message}` });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AdminIcon />
          Admin Panel
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {message.text && (
          <Alert 
            severity={message.type} 
            sx={{ mb: 2 }}
            onClose={() => setMessage({ type: '', text: '' })}
          >
            {message.text}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)}>
            <Tab icon={<PersonIcon />} label="Users" />
            <Tab icon={<BusinessIcon />} label="Projects" />
            <Tab icon={<AssignUserIcon />} label="Assign Users" />
            {/* <Tab icon={<SheetsIcon />} label="Google Sheets" /> */}
          </Tabs>
        </Box>

        {/* Users Tab */}
        <TabPanel value={tab} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Users Management</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setUserDialog(true)}
            >
              Create User
            </Button>
          </Box>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.is_admin ? 'Admin (All Projects)' : 'User'} 
                        color={user.is_admin ? 'secondary' : 'default'} 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.is_active ? 'Active' : 'Inactive'} 
                        color={user.is_active ? 'success' : 'error'} 
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDeleteUser(user.id, user.email)}
                        startIcon={<DeleteIcon />}
                        disabled={user.is_admin} // Prevent deleting admin users
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Projects Tab */}
        <TabPanel value={tab} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Projects Management</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setProjectDialog(true)}
            >
              Create Project
            </Button>
          </Box>
          
          <Grid container spacing={3}>
            {projects.map((project) => {
              // Tracking logic display mapping
              const getTrackingLogicInfo = (logic) => {
                switch(logic) {
                  case 'UKBZT':
                    return { label: 'UKBZ', color: 'primary', description: 'ShipStation + FedEx + EU Delivery' };
                  case 'UAET':
                    return { label: 'UAET', color: 'secondary', description: 'Aramex + UAE Delivery' };
                  case 'EZPOST':
                    return { label: 'EZPOST', color: 'success', description: 'EasyPost + US Delivery' };
                  case 'DHL':
                    return { label: 'DHLT', color: 'warning', description: 'DHL SOAP + FE Delivery' };
                  case 'AUINT':
                    return { label: 'AUINT', color: 'info', description: 'ShipStation + FedEx + FE Delivery' };
                  case 'INEZPOST':
                    return { label: 'INEZPOST', color: 'error', description: 'EZPOST + DHL + Delivery Placeholder' };
                  case 'fedUAE':
                    return { label: 'fedUAE', color: 'primary', description: 'FedEx AWB + Aramex Shipments + UAE Delivery' };
                  default:
                    return { label: logic || 'UKBZ', color: 'default', description: 'Default Logic' };
                }
              };
              
              const trackingInfo = getTrackingLogicInfo(project.tracking_logic);
              
              return (
                <Grid item xs={12} md={6} key={project.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" component="h2" gutterBottom>
                          {project.name}
                        </Typography>
                        <Chip 
                          label={trackingInfo.label} 
                          color={trackingInfo.color} 
                          size="small" 
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {project.description || 'No description'}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                          <strong>Tracking Logic:</strong> {trackingInfo.description}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          Database: {project.database_name}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          ID: {project.id}
                        </Typography>
                      </Box>
                      </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => handleEditProject(project)}
                        startIcon={<EditIcon />}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDeleteProject(project.id)}
                        startIcon={<DeleteIcon />}
                      >
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </TabPanel>

        {/* Assign Users Tab */}
        <TabPanel value={tab} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Assign Users to Projects</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={loadAssignments}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAssignDialog(true)}
              >
                Assign User
              </Button>
            </Box>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Use this section to assign users to specific projects. Users will only be able to access projects they are assigned to.
          </Typography>
          
          {/* Current Assignments Table */}
          <Typography variant="h6" sx={{ mb: 2 }}>Current Assignments ({assignments.filter(a => !a.is_user_admin).length})</Typography>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Assigned Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">No assignments found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  assignments.filter(assignment => !assignment.is_user_admin).map((assignment) => (
                    <TableRow key={assignment.id}>
                      <TableCell>{assignment.user_name}</TableCell>
                      <TableCell>{assignment.user_email}</TableCell>
                      <TableCell>{assignment.project_name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={assignment.role} 
                          size="small" 
                          color={assignment.role === 'admin' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {assignment.created_at ? new Date(assignment.created_at).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleRemoveAssignment(assignment)}
                          startIcon={<DeleteIcon />}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Remove Google Sheets Tab - integration is only for OTDR dashboard */}
        {/* <TabPanel value={tab} index={3}>
          <GoogleSheetsIntegration projects={projects} />
        </TabPanel> */}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Create User Dialog */}
      <Dialog open={userDialog} onClose={() => setUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={userForm.email}
            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Username"
            value={userForm.username}
            onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={userForm.password}
            onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
            margin="normal"
            required
          />
          <FormControlLabel
            control={
              <Switch
                checked={userForm.is_admin}
                onChange={(e) => setUserForm({ ...userForm, is_admin: e.target.checked })}
              />
            }
            label="Admin User"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Create Project Dialog */}
      <Dialog open={projectDialog} onClose={() => setProjectDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Project Name"
            value={projectForm.name}
            onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            value={projectForm.description}
            onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
            margin="normal"
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Tracking Logic</InputLabel>
            <Select
              value={projectForm.tracking_logic}
              onChange={(e) => handleTrackingLogicChange(e.target.value)}
            >
              <MenuItem value="UKBZT">UKBZT (ShipStation + FedEx + EU Delivery API)</MenuItem>
              <MenuItem value="DHL">DHL (ShipStation + DHL SOAP + FE Delivery API)</MenuItem>
              <MenuItem value="UAET">UAET (Aramex + UAE Delivery API)</MenuItem>
              <MenuItem value="EZPOST">EZPOST (EasyPost API)</MenuItem>
              <MenuItem value="AUINT">AUINT (ShipStation + FedEx + FE Delivery API)</MenuItem>
              <MenuItem value="INEZPOST">INEZPOST (EZPOST Shipments + DHL AWB + Delivery Placeholder)</MenuItem>
              <MenuItem value="fedUAE">fedUAE (FedEx AWB + Aramex Shipments + UAE Delivery)</MenuItem>
              {/* Add more tracking logics here as they become available */}
            </Select>
          </FormControl>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>API Credentials</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure API credentials for the selected tracking logic. Leave empty to use default environment variables.
            {projectForm.tracking_logic === 'DHL' && ' (DHL tracking uses public API - no AWB credentials needed)'}
            {projectForm.tracking_logic === 'UAET' && ' (UAET uses Aramex API for all tracking operations)'}
            {projectForm.tracking_logic === 'EZPOST' && ' (EZPOST uses EasyPost API for all tracking operations)'}
            {projectForm.tracking_logic === 'INEZPOST' && ' (INEZPOST combines EZPOST shipments + DHL AWB tracking + delivery placeholder)'}
            {projectForm.tracking_logic === 'AUINT' && ' (AUINT uses ShipStation + FedEx APIs with FE delivery system)'}
            {projectForm.tracking_logic === 'fedUAE' && ' (fedUAE uses FedEx for AWB tracking + Aramex for shipment tracking + UAE delivery)'}
          </Typography>
          
          <Grid container spacing={2}>
            {/* ShipStation and Delivery API fields - not needed for UAET, EZPOST, INEZPOST, or fedUAE */}
            {projectForm.tracking_logic !== 'UAET' && projectForm.tracking_logic !== 'EZPOST' && projectForm.tracking_logic !== 'INEZPOST' && projectForm.tracking_logic !== 'fedUAE' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="ShipStation API Key"
                    value={projectForm.tracking_credentials.shipstation_api_key}
                    onChange={(e) => setProjectForm({ 
                      ...projectForm, 
                      tracking_credentials: {
                        ...projectForm.tracking_credentials,
                        shipstation_api_key: e.target.value
                      }
                    })}
                    margin="normal"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Delivery API Key"
                    value={projectForm.tracking_credentials.delivery_api_key}
                    onChange={(e) => setProjectForm({ 
                      ...projectForm, 
                      tracking_credentials: {
                        ...projectForm.tracking_credentials,
                        delivery_api_key: e.target.value
                      }
                    })}
                    margin="normal"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Delivery API Token"
                    value={projectForm.tracking_credentials.delivery_api_token}
                    onChange={(e) => setProjectForm({ 
                      ...projectForm, 
                      tracking_credentials: {
                        ...projectForm.tracking_credentials,
                        delivery_api_token: e.target.value
                      }
                    })}
                    margin="normal"
                    size="small"
                  />
                </Grid>
              </>
            )}
            
            {/* Show FedEx fields for UKBZT, AUINT, and fedUAE logic */}
            {(projectForm.tracking_logic === 'UKBZT' || projectForm.tracking_logic === 'AUINT' || projectForm.tracking_logic === 'fedUAE') && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="FedEx Client ID"
                    value={projectForm.tracking_credentials.fedex_client_id}
                    onChange={(e) => setProjectForm({ 
                      ...projectForm, 
                      tracking_credentials: {
                        ...projectForm.tracking_credentials,
                        fedex_client_id: e.target.value
                      }
                    })}
                    margin="normal"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="FedEx Client Secret"
                    type="password"
                    value={projectForm.tracking_credentials.fedex_client_secret}
                    onChange={(e) => setProjectForm({ 
                      ...projectForm, 
                      tracking_credentials: {
                        ...projectForm.tracking_credentials,
                        fedex_client_secret: e.target.value
                      }
                    })}
                    margin="normal"
                    size="small"
                  />
                </Grid>
              </>
            )}
            
            {/* Show Aramex fields for UAET and fedUAE logic */}
            {(projectForm.tracking_logic === 'UAET' || projectForm.tracking_logic === 'fedUAE') && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Aramex Username"
                    value={projectForm.tracking_credentials.aramex_username}
                    onChange={(e) => setProjectForm({ 
                      ...projectForm, 
                      tracking_credentials: {
                        ...projectForm.tracking_credentials,
                        aramex_username: e.target.value
                      }
                    })}
                    margin="normal"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Aramex Password"
                    type="password"
                    value={projectForm.tracking_credentials.aramex_password}
                    onChange={(e) => setProjectForm({ 
                      ...projectForm, 
                      tracking_credentials: {
                        ...projectForm.tracking_credentials,
                        aramex_password: e.target.value
                      }
                    })}
                    margin="normal"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Aramex Account Number"
                    value={projectForm.tracking_credentials.aramex_account_number}
                    onChange={(e) => setProjectForm({ 
                      ...projectForm, 
                      tracking_credentials: {
                        ...projectForm.tracking_credentials,
                        aramex_account_number: e.target.value
                      }
                    })}
                    margin="normal"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Aramex Account PIN"
                    value={projectForm.tracking_credentials.aramex_account_pin}
                    onChange={(e) => setProjectForm({ 
                      ...projectForm, 
                      tracking_credentials: {
                        ...projectForm.tracking_credentials,
                        aramex_account_pin: e.target.value
                      }
                    })}
                    margin="normal"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Aramex Account Entity"
                    value={projectForm.tracking_credentials.aramex_account_entity}
                    onChange={(e) => setProjectForm({ 
                      ...projectForm, 
                      tracking_credentials: {
                        ...projectForm.tracking_credentials,
                        aramex_account_entity: e.target.value
                      }
                    })}
                    margin="normal"
                    size="small"
                    placeholder="DXB"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Aramex Account Country"
                    value={projectForm.tracking_credentials.aramex_account_country}
                    onChange={(e) => setProjectForm({ 
                      ...projectForm, 
                      tracking_credentials: {
                        ...projectForm.tracking_credentials,
                        aramex_account_country: e.target.value
                      }
                    })}
                    margin="normal"
                    size="small"
                    placeholder="AE"
                  />
                </Grid>
              </>
            )}
            
            {/* Show EasyPost field for EZPOST and INEZPOST logic */}
            {(projectForm.tracking_logic === 'EZPOST' || projectForm.tracking_logic === 'INEZPOST') && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="EasyPost API Key"
                  value={projectForm.tracking_credentials.easypost_api_key}
                  onChange={(e) => setProjectForm({ 
                    ...projectForm, 
                    tracking_credentials: {
                      ...projectForm.tracking_credentials,
                      easypost_api_key: e.target.value
                    }
                  })}
                  margin="normal"
                  size="small"
                  type="password"
                />
              </Grid>
            )}
          </Grid>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Google Sheets Integration</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={projectForm.enable_google_sheets || false}
                onChange={(e) => setProjectForm({ 
                  ...projectForm, 
                  enable_google_sheets: e.target.checked 
                })}
              />
            }
            label="Enable Google Sheets integration for this project"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            When enabled, you can configure Google Sheets synchronization for this project in the Google Sheets tab after creation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateProject} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={editProjectDialog} onClose={() => setEditProjectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Project Name"
            value={editProjectForm.name}
            onChange={(e) => setEditProjectForm({ ...editProjectForm, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            value={editProjectForm.description}
            onChange={(e) => setEditProjectForm({ ...editProjectForm, description: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditProjectDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateProject} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Assign User Dialog */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign User to Project</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>User</InputLabel>
            <Select
              value={assignForm.user_id}
              onChange={(e) => setAssignForm({ ...assignForm, user_id: e.target.value })}
            >
              {users.filter(user => !user.is_admin).map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.username} ({user.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Project</InputLabel>
            <Select
              value={assignForm.project_id}
              onChange={(e) => setAssignForm({ ...assignForm, project_id: e.target.value })}
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={assignForm.role}
              onChange={(e) => setAssignForm({ ...assignForm, role: e.target.value })}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(false)}>Cancel</Button>
          <Button onClick={handleAssignUser} variant="contained">Assign</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}